import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

// ─── HU-090: tela de aceite de convite ────────────────────────────────────────

export default function AceitarConvite() {
  const [, navigate] = useLocation();
  const [, params] = useRoute("/convite/:token");
  const token = params?.token ?? "";
  const { login: authLogin } = useAuth();

  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [showPass, setShowPass]   = useState(false);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);

  const { data: invite, isLoading } = trpc.clientAuth.getInvite.useQuery(
    { token },
    { enabled: !!token, retry: false }
  );
  const acceptMutation = trpc.clientAuth.acceptInvite.useMutation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("A senha deve ter no mínimo 8 caracteres."); return; }
    if (password !== confirm) { setError("As senhas não coincidem."); return; }
    setLoading(true);
    try {
      const res = await acceptMutation.mutateAsync({ token, password });
      authLogin({
        token: res.token,
        userId: res.userId,
        name: res.name ?? "",
        email: res.email,
        role: (res.role ?? "viewer") as "admin" | "editor" | "viewer",
        clientId: res.clientId ?? null,
        isRazAdmin: false,
      });
      navigate("/");
    } catch (err: any) {
      setError(err.message ?? "Erro ao aceitar o convite.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--solar-bg)]">
      <div className="relative flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <div className="mb-10 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-[var(--solar-ink)] shadow-[var(--elev-2)]">
            <span className="text-white font-extrabold text-2xl leading-none">s</span>
          </div>
          <p className="text-[var(--solar-ink)] text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>SocialHub</p>
          <p className="text-[var(--solar-mute)] text-[11px] tracking-[0.2em] uppercase font-medium">Gestão de Social Media</p>
        </div>

        <div className="w-full max-w-md">
          <div className="rounded-xl border border-[var(--solar-line)] bg-white shadow-[var(--elev-2)] overflow-hidden p-8">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-8 h-8 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--brand)] animate-spin" />
              </div>
            ) : !invite || !invite.valid ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-[var(--destructive)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-[var(--solar-ink)] text-lg font-semibold mb-2">Convite indisponível</h2>
                <p className="text-[var(--ink-2)] text-sm mb-6">
                  {invite?.reason === "expired"
                    ? "Este convite expirou. Peça um novo convite ao administrador."
                    : invite?.reason === "used"
                    ? "Este convite já foi utilizado. Faça login normalmente."
                    : "Link de convite inválido ou não encontrado."}
                </p>
                <button onClick={() => navigate("/login")}
                  className="text-[var(--solar-mute)] text-sm hover:text-[var(--solar-ink)] transition">
                  ← Ir para o login
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-[var(--solar-ink)] text-xl font-semibold mb-1">Bem-vindo(a), {invite.name}!</h1>
                <p className="text-[var(--ink-2)] text-sm mb-6">
                  Crie uma senha para ativar seu acesso — <span className="text-[var(--solar-ink)] font-medium">{invite.email}</span>
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[11px] mb-1.5 uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Senha</label>
                    <div className="relative">
                      <input
                        type={showPass ? "text" : "password"}
                        required
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Mínimo 8 caracteres"
                        className="w-full bg-white border border-[var(--solar-line)] text-[var(--solar-ink)] rounded-[10px] px-4 py-3 text-sm placeholder-[var(--solar-mute)] focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition pr-10"
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition">
                        {showPass ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] mb-1.5 uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Confirmar senha</label>
                    <input
                      type={showPass ? "text" : "password"}
                      required
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="Repita a senha"
                      className="w-full bg-white border border-[var(--solar-line)] text-[var(--solar-ink)] rounded-[10px] px-4 py-3 text-sm placeholder-[var(--solar-mute)] focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition"
                    />
                  </div>
                  {error && <p className="text-[var(--destructive)] text-sm">{error}</p>}
                  <button type="submit" disabled={loading}
                    className="w-full bg-[var(--solar-ink)] text-white font-semibold py-3 rounded-[10px] text-sm shadow-[var(--elev-1)] hover:opacity-90 transition disabled:opacity-50">
                    {loading ? "Ativando acesso..." : "Ativar acesso →"}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-center text-[var(--solar-mute)] text-xs mt-6">
            Problemas?{" "}
            <a href="mailto:rafael@razconsulting.com.br" className="text-[var(--solar-ink)] hover:underline transition">
              rafael@razconsulting.com.br
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
